'use client';

import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Modal } from '@/components/antd-wrapper/modal/global/modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Loader2, LockKeyhole } from 'lucide-react';
import Pagination from '@/components/ui/pagination';
import { Tooltip } from 'antd';
import { getUserPointsDetails } from '@/services/space-mange';
import { useUserInfoContext } from '@/contexts/user-info-context';
import { useSpacePointsContext } from '@/contexts/space-points-context';
import Point from '/public/point.svg';
import { triggerTemplateDetail } from '@/components/deep-mining/template/template-detail-modal';
import FormatTooltipContent from '@/components/deep-mining/format-tooltip-content';
import {
  getSpaceConfig,
  getTooltipConfig,
} from '@/lib/utils/space-points-config';
import { cn } from '@/lib/utils';
import { includes } from 'lodash';
import { toast } from 'sonner';
import { triggerCustomerModal } from '../antd-wrapper/modal/contact/customer-manager';
import QuestionMarkIcon from '/public/question-mark.svg';
import { useI18n } from '@/hooks/use-i18n';

interface PointsTransactionsItem {
  id: string;
  title: string;
  time: string;
  type: string;
  points: number;
  is_deleted: boolean; // 是否已删除
  obj_id: string;
  space_change: {
    space_same: boolean;
    space_name: string;
  };
}

const clickableTypes = [
  'thread',
  'template',
  'task_pre_deduction',
  'task_compensation',
  'gaoding_edit_img',
  'gaoding_edit_temp',
];
// 自定义事件名称
export const OPEN_POINTS_DETAILS_MODAL_EVENT = 'openPointsDetailsModal';

// 触发积分详情弹窗的辅助函数
export function triggerPointsDetailsModal() {
  const event = new Event(OPEN_POINTS_DETAILS_MODAL_EVENT);
  window.dispatchEvent(event);
}

export function PointsDetailsModal() {
  const { t } = useI18n(['components', 'common']);
  const { spacePoints, isLoading: isSpacePointsLoading } =
    useSpacePointsContext();
  // 内部状态管理
  const [isOpen, setIsOpen] = useState(false);
  const [pointsTransactions, setPointsTransactions] = useState<
    PointsTransactionsItem[]
  >([]);

  // 分页
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // loading
  const [loading, setLoading] = useState(false);

  // 监听自定义事件，用于触发积分详情弹窗
  useEffect(() => {
    const handleOpenPointsDetailsModal = () => {
      setIsOpen(true);
    };

    // 添加事件监听器
    window.addEventListener(
      OPEN_POINTS_DETAILS_MODAL_EVENT,
      handleOpenPointsDetailsModal,
    );

    // 清理函数
    return () => {
      window.removeEventListener(
        OPEN_POINTS_DETAILS_MODAL_EVENT,
        handleOpenPointsDetailsModal,
      );
    };
  }, []);
  // const indexOfLastItem = currentPage * itemsPerPage;
  // const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPointsTransactions = pointsTransactions;
  const totalPages = Math.ceil(total / itemsPerPage);

  const fetchPointsTransactions = async () => {
    setLoading(true);
    const response = await getUserPointsDetails({
      page_no: currentPage,
      page_size: itemsPerPage,
    });
    setLoading(false);

    setPointsTransactions(response.list);
    setTotal(response.total);

    // 检查当前页码是否有效，如果无效则调整到最后一页
    const newTotalPages = Math.ceil(response.total / itemsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }
  };

  // 获取积分列表
  useEffect(() => {
    if (!isOpen) return;
    fetchPointsTransactions();
  }, [currentPage, itemsPerPage, isOpen]);

  const renderTooltip = (type: string) => {
    const config = getTooltipConfig(type);
    if (!config) return null;

    return (
      <Tooltip title={FormatTooltipContent(config)}>
        <QuestionMarkIcon className="w-4 h-4 text-[rgba(0,0,0,0.45)] ml-1" />
      </Tooltip>
    );
  };

  const config = getSpaceConfig(spacePoints?.space_type, spacePoints?.status);

  // 首先，在组件内部添加一个类型到显示文本前缀的映射表
  const typePrefixMap: Record<string, string> = {
    template: t('components:points.templateUsage'),
    task_pre_deduction: t('components:points.asyncTask'),
    task_compensation: t('components:points.pointsRefund'),
    gaoding_edit_img: t('components:points.gaodingEditImg'),
    gaoding_edit_temp: t('components:points.gaodingEditTemp'),
  };

  // 然后添加一个获取显示文本的辅助函数
  const getItemDisplayText = (item: PointsTransactionsItem): string => {
    // 特殊处理积分退回类型，根据points值的正负决定显示文本
    if (item.type === 'task_compensation') {
      const prefix =
        item.points > 0
          ? t('components:points.pointsRefund')
          : t('components:points.asyncTask');
      return `${prefix}（${item.title || '-'}）`;
    }
    const prefix = typePrefixMap[item.type];
    if (prefix) {
      return `${prefix}（${item.title || '-'}）`;
    }
    return item.title || '-';
  };

  // 处理积分项目点击事件
  const handleJump = (item: PointsTransactionsItem) => {
    // 定义不同类型的配置
    const actionTypes = {
      agent: {
        deletedMessage: t('components:points.conversationDeleted'),
        action: (id: string) => window.open(`/agents/${id}`, '_blank'),
      },
      template: {
        deletedMessage: t('components:points.templateDeleted'),
        action: (id: string) =>
          triggerTemplateDetail({ token: id, hiddenFooter: true }),
      },
    };

    // 类型到操作类型的映射
    const typeToActionMap: Record<string, keyof typeof actionTypes> = {
      thread: 'agent',
      task_pre_deduction: 'agent',
      task_compensation: 'agent',
      template: 'template',
      gaoding_edit_img: 'agent',
      gaoding_edit_temp: 'agent',
    };

    const actionType = typeToActionMap[item.type];
    const config = actionType ? actionTypes[actionType] : null;

    if (!config) return;

    if (item.is_deleted) {
      toast.error(config.deletedMessage);
      return;
    }
    if (!item?.space_change?.space_same) {
      toast.error(
        t('components:points.conversationMigrated', {
          spaceName: item?.space_change?.space_name,
        }),
      );
      return;
    }

    config.action(item.obj_id);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      title={t('components:points.pointsDetails')}
      width={800}
      height={560}
      footer={null}
      className="points-details-modal"
      classNames={{
        content: '!pb-[16px]',
        body: 'overflow-y-auto',
      }}
      zIndex={1000}
    >
      <div className="flex flex-col h-full gap-4 px-6">
        <div className="bg-[#F4F4F5] rounded-md px-4">
          <div
            className="flex justify-between items-center text-[16px] text-[rgba(0,0,0,0.75)] font-bold py-4 border-b border-dashed border-[rgba(0,0,0,0.1)]
"
          >
            <div className="text-[16px] text-[rgba(0,0,0,0.75)] font-bold ">
              {config.displayName()}
            </div>
            <Button
              className="cursor-pointer h-6 px-3 py-3 text-[15px] rounded-[100px] hover:bg-[#5959d9]"
              onClick={() => {
                const modalConfig = config.modalConfig();
                triggerCustomerModal({
                  title: modalConfig.title,
                  data: modalConfig.data,
                });
              }}
            >
              {config.buttonText()}
            </Button>
          </div>
          <div className="pb-3">
            {spacePoints?.detail?.map((pointDetail, index) => (
              <div
                key={pointDetail.type || index}
                className="flex flex-col gap-1 pt-3"
              >
                <div className="flex justify-between items-center">
                  <div className="flex justify-between items-center text-[14px] text-[rgba(0,0,0,0.75)] font-bold">
                    <Point className="w-5 h-5 mr-2" />
                    {pointDetail.name}
                    {renderTooltip(pointDetail.type)}
                  </div>
                  <div className="text-[14px] text-[rgba(0,0,0,0.75)] font-bold text-right">
                    {spacePoints.status === 'block' ? (
                      <LockKeyhole className="w-3 h-3" />
                    ) : (
                      pointDetail.balance
                    )}
                  </div>
                </div>
                {pointDetail.children?.length > 0
                  ? pointDetail.children?.map((childPoint, childIndex) => (
                      <div
                        key={childPoint.type || childIndex}
                        className="flex justify-between items-center text-[13px] text-[rgba(0,0,0,0.45)]"
                      >
                        <div>{childPoint.name}</div>
                        <div className="text-right">{childPoint.balance}</div>
                      </div>
                    ))
                  : pointDetail.description && (
                      <div className="text-[13px] text-[rgba(0,0,0,0.45)]">
                        {pointDetail.description}
                      </div>
                    )}
              </div>
            ))}
          </div>
        </div>

        {/* 积分列表 */}
        <div className="rounded-md border flex-1">
          <Table className="border-collapse rounded-md overflow-hidden">
            <TableHeader className="bg-white">
              <TableRow className="sticky top-0 bg-white z-10 hover:bg-white">
                <TableHead className="px-4 py-2">
                  {t('common:details')}
                </TableHead>
                <TableHead className="hidden md:table-cell px-4 py-2 relative after:content-[''] after:block after:h-full after:bg-[#E5E7EB] after:z-11 after:absolute after:left-0 after:w-[1px] after:top-0">
                  {t('common:date')}
                </TableHead>
                <TableHead className="text-right px-4 py-2 relative after:content-[''] after:block after:h-full after:bg-[#E5E7EB] after:z-11 after:absolute after:left-0 after:w-[1px] after:top-0">
                  {t('components:points.pointsChange')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="text-sm font-normal bg-[#FAFAFA]">
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground py-8"
                  >
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      <span>{t('common:loading')}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : currentPointsTransactions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground py-8"
                  >
                    {t('common:noData')}
                  </TableCell>
                </TableRow>
              ) : (
                currentPointsTransactions.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="w-[300px] min-w-[300px] max-w-[300px] px-4 py-3 box-border">
                      {includes(clickableTypes, item.type) ? (
                        <div
                          className="truncate cursor-pointer hover:text-primary transition-colors"
                          title={getItemDisplayText(item)}
                          onClick={() => handleJump(item)}
                        >
                          {getItemDisplayText(item)}
                        </div>
                      ) : (
                        <div
                          className="truncate"
                          title={getItemDisplayText(item)}
                        >
                          {getItemDisplayText(item)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="w-[200px] px-4 py-3">
                      {item.time
                        ? dayjs(item.time).format('YYYY-MM-DD HH:mm:ss')
                        : '-'}
                    </TableCell>
                    <TableCell className="w-[96px] px-4 py-3 text-right">
                      <div
                        className={cn(
                          'line-clamp-2 break-all whitespace-normal',
                          item.points > 0 && 'text-primary ',
                        )}
                      >
                        {`${item.points > 0 ? '+' : ''}${item.points}`}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="flex justify-end min-h-[38px]">
            {/* 分页控制 */}
            <div className="">
              <Pagination
                page={currentPage}
                count={totalPages}
                onChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
